/**
 * capabilityApi — factory binding the BE capability REST surface to the
 * shared `ConnectorApi<TConn>` shape consumed by `<ConnectorWizard>`.
 *
 * Per CLAUDE.md "white-label naming OBBLIGATORIO": the FE addresses
 * capability slugs, NEVER vendor names.
 *
 * May 19, 2026 — the BE shipped the unified
 * `/api/v1/connectors/{capability}/*` surface (commit cdb0999 in
 * ando-agent: `start`, `connections`, `status`, `DELETE {conn_id}`).
 * Per-vendor probes (`test` / `sync_now` / `discover` / `schema`) stay
 * on the legacy `/api/v1/{vendor}/*` routes — those are where the actual
 * implementations live. This adapter:
 *
 *   - `getStatus`        → `GET /{capability}/status`
 *   - `listConnections`  → `GET /{capability}/connections`
 *   - `connect`          → `POST /{capability}/start`
 *                          (response: `{ flow_type, oauth_url?, form_schema? }`,
 *                           shimmed to the FE `ConnectResult` shape)
 *   - `disconnect`       → `DELETE /{capability}/{conn_id}`
 *   - `sync` / `test`    → legacy per-vendor routes (FE retries the
 *                          per-vendor URL because the unified router
 *                          doesn't expose them — by design).
 *
 * Why factories instead of one-class-per-capability: the wire shape is
 * identical across capabilities (status / list / connect / disconnect /
 * sync). Per-capability tweaks (BYOA client_id, sandbox flag, …) live in
 * the spec.steps, not in the API layer.
 */

import { type CapabilityName, type StartFlowResponse, startCapabilityFlow } from "@ando/core";
import { apiClient } from "@ando/core/api";
import type {
  BaseConnection,
  CapabilitySlug,
  ConnectOpts,
  ConnectResult,
  ConnectorApi,
  ConnectorStatus,
} from "@ando/ui";

interface CapabilityApiOptions<TConn extends BaseConnection> {
  readonly capability: CapabilitySlug;
  /**
   * BE-side vendor hint when the capability is single-vendor (e.g.
   * `accounting → "fatture_in_cloud"`). For multi-vendor capabilities the
   * adapter pulls the vendor out of `opts.vendor` at connect-time so the
   * route can dispatch.
   */
  readonly defaultVendor?: string;
  /**
   * Optional row mapper — useful when the BE returns a slightly different
   * shape than `TConn`. Defaults to identity.
   */
  readonly mapConnection?: (raw: unknown) => TConn;
}

/**
 * Resolve the vendor slug for a connect() call.
 *
 * May 24, 2026 — the previous version THREW when ``opts.vendor`` was
 * absent. That made multi-vendor capabilities (messaging, drive, email,
 * accounting) unusable from the new ``$capability`` page: clicking
 * "Connect" without first choosing a vendor surfaced a scary banner
 * "Vendor mancante per capability X" with no recovery path.
 *
 * NEW contract — caller MUST pick the vendor BEFORE invoking
 * ``connect()`` (the AddAccountMenu enforces this by handing a vendor
 * slug into the dialog). To prevent silent regressions:
 *   - DEV: log a console error so the caller can see the misuse.
 *   - PROD: return the empty string; the BE will reject the request
 *     with a typed error which surfaces as a toast, not as a banner
 *     embedded in the page chrome.
 *
 * BE endpoint pattern: today's pages target per-vendor REST surfaces; we
 * front them with one capability-aware adapter so the FE never speaks
 * the vendor name. Switch to a unified shape when BE ships
 * ``/api/v1/connectors/{capability}/*``.
 */
function vendorOf(capability: CapabilitySlug, opts: ConnectOpts, fallback?: string): string {
  const v = (opts.vendor as string | undefined) ?? fallback;
  if (!v) {
    if (import.meta.env.DEV) {
      console.error(
        `[capabilityApi] connect() invoked without a vendor for capability=${capability}. Caller must pre-select a vendor via the AddAccountMenu (or pass spec.vendors=[single] for single-vendor specs).`,
      );
    }
    return "";
  }
  return v;
}

export function createCapabilityApi<TConn extends BaseConnection>(
  options: CapabilityApiOptions<TConn>,
): ConnectorApi<TConn> {
  const { capability, defaultVendor, mapConnection } = options;
  // ``apiClient`` already prepends the ``/api/v1`` base from ``getEnv().apiUrl``.
  // The previous ``/api/v1/connectors/...`` literal here doubled the prefix to
  // ``/api/v1/api/v1/connectors/...`` (404) — keep the path relative.
  const base = `/connectors/${capability}`;

  return {
    async getStatus(): Promise<ConnectorStatus> {
      // BE ``GET /connectors/{capability}/status`` returns the *health*
      // shape (`{capability, state, count, error_count}`) — it does NOT
      // carry an `enabled` boolean (license-gating is a v2-only concept
      // not yet wired BE-side, May 2026). Normalise here so the wizard
      // banner (`status.enabled === false`) doesn't false-positive on
      // every capability the BE happily responds to.
      try {
        const raw = await apiClient.get<Record<string, unknown>>(`${base}/status`);
        // Forward any explicit `enabled` if the BE ever starts shipping
        // one (forward-compat). Otherwise infer: a capability the BE
        // answers about (state in {connected, partial, disconnected}) is
        // enabled — the "disabled" / license-gated case is a 403/404.
        if (typeof raw?.enabled === "boolean") return raw as ConnectorStatus;
        return { ...raw, enabled: true } as ConnectorStatus;
      } catch {
        return { enabled: true };
      }
    },

    async listConnections(): Promise<readonly TConn[]> {
      try {
        // May 22, 2026 — BE ships `{ connections: [...] }`, NOT a bare
        // array. The wizard previously crashed with `t.map is not a
        // function` on click because we were trying to call `.map` on the
        // wrapper object. Unwrap defensively: accept either shape so the
        // adapter stays forward-compat if the BE ever flattens (it won't,
        // per `/connectors/{cap}/connections` OpenAPI contract).
        const raw = await apiClient.get<unknown>(`${base}/connections`);
        const list: readonly unknown[] = Array.isArray(raw)
          ? raw
          : ((raw as { connections?: readonly unknown[] })?.connections ?? []);
        if (mapConnection) return list.map(mapConnection);
        return list as readonly TConn[];
      } catch {
        return [];
      }
    },

    async connect(_userId: string, opts: ConnectOpts): Promise<ConnectResult> {
      const vendor = vendorOf(capability, opts, defaultVendor);
      // BE unified surface: POST /connectors/{capability}/start returns a
      // discriminated `StartFlowResponse`. Shim into the canonical
      // ``ConnectResult`` shape from ``@ando/ui/connectors/types``:
      //   { redirect_url?: string; connection_id?: string }
      // The wizard checks ``result.redirect_url`` to fire
      // ``window.location.href = ...`` — the previous return shape
      // (``{ status, oauth_url }``) had ZERO field overlap so OAuth
      // never launched (May 24, 2026 audit B1). ``user_id`` is
      // resolved BE-side from the session cookie.
      const flow: StartFlowResponse = await startCapabilityFlow(capability as CapabilityName, {
        vendor,
      });
      if (flow.flow_type === "oauth_redirect") {
        return { redirect_url: flow.oauth_url };
      }
      // ``credentials_form`` flows return to the wizard which renders the
      // form_schema step locally — no redirect. Return empty so the
      // wizard's ``submit()`` resolves and the credentials step mounts.
      return {};
    },

    async disconnect(connectionKey: string): Promise<void> {
      // BE unified DELETE /connectors/{capability}/{conn_id} — delegates to
      // UnifiedConnectionService.delete_connection for cross-vendor token
      // revocation + row removal.
      await apiClient.delete<void>(`${base}/${encodeURIComponent(connectionKey)}`);
    },

    async sync(connectionKey: string): Promise<{ readonly in_flight: boolean }> {
      // Legacy per-vendor route — the unified router intentionally does
      // NOT expose `sync` because the implementation is vendor-specific
      // (FiC pagination, Salesforce delta query, ...). Falls back to the
      // legacy route bound by `defaultVendor`.
      const vendor = defaultVendor ?? capability;
      return apiClient.post<{ readonly in_flight: boolean }>(
        `/${vendor}/connections/${encodeURIComponent(connectionKey)}/sync`,
        {},
      );
    },

    async test(connectionKey: string): Promise<{ readonly ok: boolean; readonly error?: string }> {
      // Same as `sync`: vendor-specific probe, kept on the legacy surface.
      const vendor = defaultVendor ?? capability;
      return apiClient.post<{ readonly ok: boolean; readonly error?: string }>(
        `/${vendor}/connections/${encodeURIComponent(connectionKey)}/test`,
        {},
      );
    },
  };
}
